import { useState } from 'react';
import { X } from 'lucide-react';
import { Card, CardContent } from '@mustauto/ui/web';
import {
  Table, TableHeader, TableBody, TableRow, TableHead, TableCell,
} from '@mustauto/ui/web';
import type { PurchaseOrder } from '@/hooks/usePurchaseOrders';
import { formatDateBR } from '@mustauto/utils';
import { formatReais } from '@/lib/utils';
import { POStatusBadge } from './StatusBadge';
import { useEscapeKey } from '@/hooks/useEscapeKey';
import { useFocusTrap } from '@/hooks/useFocusTrap';
import { useCan } from '@/hooks/useCan';
import { WhoCanHint } from '@/components/WhoCanHint';

interface PurchaseOrderModalProps {
  order: PurchaseOrder;
  onClose: () => void;
  onApprove: (id: string) => Promise<void>;
  onReject: (id: string, reason: string) => Promise<void>;
  onConfirmOrder: (id: string) => Promise<void>;
  onReceive: (order: PurchaseOrder) => void;
  onEdit: (order: PurchaseOrder) => void;
  isApproving: boolean;
  isConfirming: boolean;
}

export function PurchaseOrderModal({
  order,
  onClose,
  onApprove,
  onReject,
  onConfirmOrder,
  onReceive,
  onEdit,
  isApproving,
  isConfirming,
}: PurchaseOrderModalProps) {
  useEscapeKey(onClose);
  const trapRef = useFocusTrap<HTMLDivElement>();
  const items = order.items ?? [];
  const [showRejectForm, setShowRejectForm] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [isRejecting, setIsRejecting] = useState(false);
  const trimmedReason = rejectReason.trim();
  const can = useCan();

  async function handleRejectConfirm() {
    if (!trimmedReason) return;
    setIsRejecting(true);
    try {
      await onReject(order.id, trimmedReason);
    } finally {
      setIsRejecting(false);
    }
  }

  return (
    <div ref={trapRef} role="dialog" aria-modal="true" className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <Card className="w-full max-w-4xl mx-4 max-h-[90vh] overflow-y-auto">
        <CardContent className="pt-6">
          {/* Header */}
          <div className="flex items-start justify-between mb-6">
            <div>
              <div className="flex items-center gap-3">
                <h2 className="text-lg font-bold">OC #{order.number}</h2>
                <POStatusBadge status={order.status} />
              </div>
              <p className="text-sm text-muted-foreground mt-1">
                Fornecedor: <span className="font-medium text-foreground">{order.supplierName ?? '—'}</span>
              </p>
              {order.createdByName && (
                <p className="text-xs text-muted-foreground mt-0.5">
                  Criada por {order.createdByName} em {new Date(order.createdAt).toLocaleDateString('pt-BR')}
                </p>
              )}
            </div>
            <button onClick={onClose} aria-label="Fechar" className="rounded-md p-1 hover:bg-muted transition-colors">
              <X size={18} />
            </button>
          </div>

          {/* Meta info */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
            <InfoBlock label="Valor total" value={order.totalAmount == null ? '—' : formatReais(order.totalAmount)} />
            <InfoBlock label="Itens" value={String(order.itemsCount)} />
            <InfoBlock
              label="Previsão entrega"
              value={order.expectedDelivery ? formatDateBR(order.expectedDelivery) : '—'}
            />
            <InfoBlock
              label="Data pedido"
              value={order.orderedAt ? new Date(order.orderedAt).toLocaleDateString('pt-BR') : '—'}
            />
          </div>

          {/* Items table */}
          <div className="rounded-lg border overflow-hidden mb-6">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Item</TableHead>
                  <TableHead>Cód. peça</TableHead>
                  <TableHead className="text-right">Qtd pedida</TableHead>
                  <TableHead className="text-right">Qtd recebida</TableHead>
                  <TableHead className="text-right">Preço unit.</TableHead>
                  <TableHead className="text-right">Total</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((item) => (
                  <TableRow key={item.id}>
                    <TableCell className="font-medium">{item.itemName}</TableCell>
                    <TableCell className="text-muted-foreground">{item.partNumber ?? '—'}</TableCell>
                    <TableCell className="text-right font-mono">{item.quantityOrdered}</TableCell>
                    <TableCell className="text-right font-mono">
                      <span className={item.quantityReceived < item.quantityOrdered ? 'text-amber-600 dark:text-amber-400' : 'text-emerald-600 dark:text-emerald-400'}>
                        {item.quantityReceived}
                      </span>
                    </TableCell>
                    <TableCell className="text-right">{item.unitPrice == null ? '—' : formatReais(item.unitPrice)}</TableCell>
                    <TableCell className="text-right font-mono font-medium">
                      {item.totalPrice == null ? '—' : formatReais(item.totalPrice)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          {/* Notes */}
          {order.notes && (
            <div className="rounded-lg border p-4 mb-6">
              <h4 className="text-xs font-semibold text-muted-foreground mb-1">Observações</h4>
              <p className="text-sm whitespace-pre-wrap break-words">{order.notes}</p>
            </div>
          )}

          {/* Timestamps */}
          <div className="flex flex-wrap gap-4 text-xs text-muted-foreground mb-6">
            {order.approvedAt && (
              <span>Aprovada em {new Date(order.approvedAt).toLocaleDateString('pt-BR')}</span>
            )}
            {order.orderedAt && (
              <span>Pedida em {new Date(order.orderedAt).toLocaleDateString('pt-BR')}</span>
            )}
            {/* O rótulo segue o status: numa OC parcial ainda falta mercadoria,
                e dizer "recebida" ali afirmaria que a entrega fechou. */}
            {order.receivedAt && (
              <span>
                {order.status === 'partial' ? 'Último recebimento em ' : 'Recebida em '}
                {new Date(order.receivedAt).toLocaleDateString('pt-BR')}
              </span>
            )}
          </div>

          {/* Reject reason form (inline) */}
          {order.status === 'pending' && showRejectForm && (
            <div className="rounded-lg border border-red-200 dark:border-red-900/60 p-4 mb-4">
              <label htmlFor="reject-reason" className="block text-xs font-semibold text-muted-foreground mb-1">
                Motivo da recusa
              </label>
              <textarea
                id="reject-reason"
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                rows={3}
                maxLength={500}
                autoFocus
                placeholder="Explique por que esta OC está sendo recusada"
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm resize-none"
              />
              <div className="mt-3 flex items-center gap-2">
                <button
                  onClick={handleRejectConfirm}
                  disabled={!trimmedReason || isRejecting}
                  className="rounded-md bg-red-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-red-700 transition-colors disabled:opacity-50"
                >
                  {isRejecting ? 'Recusando...' : 'Confirmar recusa'}
                </button>
                <button
                  onClick={() => { setShowRejectForm(false); setRejectReason(''); }}
                  disabled={isRejecting}
                  className="rounded-md border px-3 py-1.5 text-sm font-medium hover:bg-muted transition-colors disabled:opacity-50"
                >
                  Cancelar
                </button>
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex items-center gap-2 border-t pt-4">
            {(order.status === 'draft' || order.status === 'approved') && can('compras.pedir') && (
              <button
                onClick={() => onEdit(order)}
                className="rounded-md border px-3 py-1.5 text-sm font-medium hover:bg-muted transition-colors"
              >
                Editar
              </button>
            )}
            {order.status === 'pending' && !can('compras.aprovar') && (
              <WhoCanHint permission="compras.aprovar" action="aprova o pedido" />
            )}
            {order.status === 'pending' && can('compras.aprovar') && (
              <>
                <button
                  onClick={() => onApprove(order.id)}
                  disabled={isApproving || isRejecting}
                  className="rounded-md bg-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-700 dark:bg-blue-700 dark:hover:bg-blue-600 transition-colors disabled:opacity-50"
                >
                  {isApproving ? 'Aprovando...' : 'Aprovar'}
                </button>
                <button
                  onClick={() => setShowRejectForm(true)}
                  disabled={isApproving || showRejectForm}
                  className="rounded-md border border-red-200 px-3 py-1.5 text-sm font-medium text-red-600 hover:bg-red-50 dark:border-red-900/60 dark:text-red-400 dark:hover:bg-red-950/40 transition-colors disabled:opacity-50"
                >
                  Recusar
                </button>
              </>
            )}
            {order.status === 'approved' && can('compras.aprovar') && (
              <button
                onClick={() => onConfirmOrder(order.id)}
                disabled={isConfirming}
                className="rounded-md bg-indigo-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-indigo-700 dark:bg-indigo-700 dark:hover:bg-indigo-600 transition-colors disabled:opacity-50"
              >
                {isConfirming ? 'Confirmando...' : 'Confirmar pedido ao fornecedor'}
              </button>
            )}
            {(order.status === 'ordered' || order.status === 'partial') && !can('estoque.movimentar') && (
              <WhoCanHint permission="estoque.movimentar" action="registra o recebimento" />
            )}
            {(order.status === 'ordered' || order.status === 'partial') && can('estoque.movimentar') && (
              <button
                onClick={() => onReceive(order)}
                className="rounded-md bg-emerald-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-emerald-700 dark:bg-emerald-700 dark:hover:bg-emerald-600 transition-colors"
              >
                Registrar recebimento
              </button>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function InfoBlock({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-lg bg-muted/50 px-3 py-2">
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="text-sm font-semibold mt-0.5">{value}</div>
    </div>
  );
}
